import React, { useState, useEffect } from 'react';
import { Search, BarChart3, Briefcase, DollarSign, Cpu, Megaphone, Users, ChevronLeft, ChevronRight, Folder, CheckCircle, Play, Lock, EyeOff } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import BackButton from '../../components/BackButton';
import AddButton from '../../components/AddButton';
import clientRoadmapService from '../../services/clientRoadmapService';
import roadmapTemplateService from '../../services/roadmapTemplateService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const ClientRoadmapOverview = () => {
  const { clientId } = useParams();
  const { darkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [isReordering, setIsReordering] = useState(false);
  const [hidingAll, setHidingAll] = useState(false);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const departments = [
    { id: 'general', name: 'General', icon: Folder },
    { id: 'sales', name: 'Sales', icon: BarChart3 },
    { id: 'operations', name: 'Operations', icon: Briefcase },
    { id: 'finance', name: 'Finance', icon: DollarSign },
    { id: 'technology', name: 'Technology', icon: Cpu },
    { id: 'marketing', name: 'Marketing', icon: Megaphone },
    { id: 'hr', name: 'HR', icon: Users }
  ];

  // Load client data
  useEffect(() => {
    const loadClient = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      try {
        const accountDoc = await getDoc(doc(db, 'accounts', clientId));
        if (accountDoc.exists()) {
          const data = accountDoc.data();
          
          setClient({
            id: accountDoc.id,
            company: data.company || 'No company'
          });
        }
      } catch (error) {
        console.error('Error loading client:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClient();
  }, [clientId]);
  
  // Load client roadmap templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  useEffect(() => {
    const loadClientRoadmap = async () => {
      if (!clientId) {
        setLoadingTemplates(false);
        return;
      }

      try {
        setLoadingTemplates(true);
        const clientRoadmap = await clientRoadmapService.getClientRoadmap(clientId);
        setTemplates(clientRoadmap);
      } catch (error) {
        console.error('Error loading client roadmap:', error);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadClientRoadmap();
  }, [clientId]);

  // Filter and sort templates based on search query and order
  const filteredTemplates = templates
    .filter(template => {
      const matchesSearch = template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           template.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      const orderA = a.order || a.allOrder || 0;
      const orderB = b.order || b.allOrder || 0;
      return orderA - orderB;
    });

  // Move template up (left arrow) or down (right arrow)
  const handleMoveTemplate = async (templateId, direction) => {
    // Get all templates sorted by order
    const sortedTemplates = [...templates].sort((a, b) => {
      const orderA = a.order || a.allOrder || 0;
      const orderB = b.order || b.allOrder || 0;
      return orderA - orderB;
    });

    const currentIndex = sortedTemplates.findIndex(t => 
      t.id === templateId || t.clientRoadmapId === templateId
    );
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= sortedTemplates.length) return;

    // Swap the templates in the array
    const temp = sortedTemplates[currentIndex];
    sortedTemplates[currentIndex] = sortedTemplates[newIndex];
    sortedTemplates[newIndex] = temp;

    // Update all order values based on new positions
    sortedTemplates.forEach((template, index) => {
      template.order = index + 1;
      // Remove old order fields if they exist
      if (template.allOrder) delete template.allOrder;
      if (template.categoryOrder) delete template.categoryOrder;
    });

    // Save to client roadmap
    try {
      await clientRoadmapService.reorderClientRoadmap(clientId, sortedTemplates);
      setTemplates(sortedTemplates);
    } catch (error) {
      console.error('Error reordering client roadmap:', error);
    }
  };

  const handleHideAll = async () => {
    if (!clientId || templates.length === 0 || hidingAll) return;
    try {
      setHidingAll(true);
      const updated = templates.map((t) => ({ ...t, status: 'hidden' }));
      await clientRoadmapService.saveClientRoadmap(clientId, updated);
      setTemplates(updated);
    } catch (error) {
      console.error('Error hiding all roadmap cards:', error);
    } finally {
      setHidingAll(false);
    }
  };

  const handleCreateNew = async () => {
    const maxOrder = Math.max(...templates.map(t => t.order || t.allOrder || 0), 0);

    const newTemplate = {
      title: 'New Template',
      department: 'general',
      description: '',
      goal: '',
      pillarType: 'Sales',
      assignees: [''],
      order: maxOrder + 1,
      portalPage: '',
      tasks: [],
      navigators: [],
      emoji: ''
    };
    
    try {
      const createdTemplate = await clientRoadmapService.addTemplateToClientRoadmap(clientId, newTemplate);
      // Reload templates
      const clientRoadmap = await clientRoadmapService.getClientRoadmap(clientId);
      setTemplates(clientRoadmap);
      // Navigate to edit the new template in client context
      navigate(`/crm/fulfillment/clientroadmaps/${clientId}/template/${createdTemplate.clientRoadmapId}`);
    } catch (error) {
      console.error('Error creating new template:', error);
    }
  };

  const departmentColors = {
    'general': 'border-gray-500 bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300',
    'sales': 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    'operations': 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
    'technology': 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    'finance': 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    'marketing': 'border-pink-500 bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',
    'hr': 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
  };

  const TemplateCard = ({ template, index }) => {
    const canMoveUp = index > 0;
    const canMoveDown = index < filteredTemplates.length - 1;

    // Determine status (default to 'not-started' if not set)
    const status = template.status || 'not-started';
    
    // Get card styling based on status
    const getCardStyles = () => {
      switch (status) {
        case 'completed':
          return 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700';
        case 'in-progress':
          return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700';
        case 'hidden':
          return 'bg-gray-50 dark:bg-gray-900/40 border-gray-300 dark:border-gray-700 opacity-70';
        default:
          return 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600';
      }
    };

    // Get status icon
    const getStatusIcon = () => {
      switch (status) {
        case 'completed':
          return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
        case 'in-progress':
          return <Play className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
        case 'hidden':
          return <EyeOff className="w-5 h-5 text-gray-400 dark:text-gray-500" />;
        default:
          return <Lock className="w-5 h-5 text-gray-400 dark:text-gray-500" />;
      }
    };

    return (
      <div 
        className={`w-full rounded-lg shadow-md transition-all duration-300 border-2 ${getCardStyles()} hover:shadow-lg hover:scale-[1.02] hover:backdrop-blur-sm cursor-pointer relative`}
        onClick={() => !isReordering && navigate(`/crm/fulfillment/clientroadmaps/${clientId}/template/${template.clientRoadmapId || template.id}`)}
      >
        <div className="p-4 h-full flex flex-col items-center justify-center relative">
          {/* Order indicator */}
          <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
            {template.order || template.allOrder || 0}
          </div>
          
          {/* Status icon in top right */}
          {!isReordering && (
            <div className="absolute top-2 right-2">
              {getStatusIcon()}
            </div>
          )}
          
          {/* Reorder arrows */}
          {isReordering && (
            <div className="absolute top-2 right-2 flex items-center space-x-1">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMoveTemplate(template.clientRoadmapId || template.id, 'up');
                }}
                disabled={!canMoveUp}
                className={`p-1 rounded ${
                  canMoveUp
                    ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer'
                    : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
                title="Move up"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMoveTemplate(template.clientRoadmapId || template.id, 'down');
                }}
                disabled={!canMoveDown}
                className={`p-1 rounded ${
                  canMoveDown
                    ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer'
                    : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
                title="Move down"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Emoji */}
          {template.emoji && (
            <div className="text-4xl mb-2">
              {template.emoji}
            </div>
          )}
          
          <h3 className="font-bold text-gray-600 dark:text-gray-300 text-xl text-center">
            {template.title}
          </h3>
          <p className={`text-sm px-3 py-1 rounded-full inline-block mt-2 ${departmentColors[template.department] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
            {departments.find(d => d.id === template.department)?.name || template.department}
          </p>
        </div>
      </div>
    );
  };

  if (loading || loadingTemplates) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <BBALoader size={48} className="mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Centered Title */}
        <div className="flex items-center justify-between mb-8">
            <div className="flex-1">
              <BackButton onClick={() => navigate('/crm/fulfillment/clientroadmaps')} />
            </div>
            <div className="flex items-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                {client ? client.company : 'Loading...'}
              </h1>
            </div>
            <div className="flex-1 flex justify-end flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={handleHideAll}
                disabled={hidingAll || templates.length === 0}
                title="Set every roadmap card to Hidden (internal only) for this client"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  hidingAll || templates.length === 0
                    ? 'opacity-50 cursor-not-allowed bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-500'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <EyeOff className="w-4 h-4 shrink-0" aria-hidden />
                {hidingAll ? 'Hiding…' : 'Hide all'}
              </button>
              <button
                type="button"
                onClick={() => setIsReordering(!isReordering)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isReordering
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {isReordering ? 'Done Reordering' : 'Reorder'}
              </button>
              <AddButton onClick={handleCreateNew} />
            </div>
          </div>

          {/* Search Section */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="text"
                className={`block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Template Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTemplates.map((template, index) => (
              <TemplateCard key={template.id} template={template} index={index} />
            ))}
          </div>

      </div>
    </div>
  );
};

export default ClientRoadmapOverview;

